import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine, Twinkle2Line} from '@dropbox/dig-icons/assets';
import {derivedThemeAtom} from 'atoms/layout';
import cx from 'classnames';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';

import styles from './StatusButton.module.css';
import {getStatusStyle, isOpenStatus, StatusButtonIcon} from './StatusButtonIcon';

/*
 * StatusButton is a button that displays a status with an icon
 *
 * @param {string} status - The status to display
 * @param {string} substatus - The optional substatus to display
 * @param {boolean} isLoading - Whether the button is in a loading state
 * @param {boolean} isDisabled - Whether the button is disabled (also removes the border)
 * @param {boolean} isLabel - Whether the button should be displayed as a label (removes the padding)
 * @param {() => void} onClick - The function to call when the button is clicked
 */
export const StatusButton = ({
  status = 'no_status',
  substatus,
  disabled,
  isLoading,
  isLabel,
  tooltipText,
  hideIcon,
  hideText,
  hideIconEnd,
  size = 'small',
  onClick,
  includeArchived,
  hoverBackgroundOnly,
  hoverChromeOnly,
  disableHoverStyles,
  nonInteractive,
  compactIconOnly,
  mediumIconOnly,
  buttonProps,
}: {
  status?: string;
  substatus?: string;
  disabled?: boolean;
  isLoading?: boolean;
  tooltipText?: string;
  isLabel?: boolean;
  size?: 'small' | 'medium';
  hideIcon?: boolean;
  hideIconEnd?: boolean;
  hideText?: boolean;
  onClick?: (e: React.SyntheticEvent) => void;
  includeArchived?: boolean;
  hoverBackgroundOnly?: boolean;
  hoverChromeOnly?: boolean;
  disableHoverStyles?: boolean;
  nonInteractive?: boolean;
  compactIconOnly?: boolean;
  mediumIconOnly?: boolean;
  buttonProps?: Record<string, unknown>;
}) => {
  const isDark = useAtomValue(derivedThemeAtom) !== 'bright';
  const hasTransparentBg =
    status === 'no_status' || status === 'not_started' || isOpenStatus(status);
  const shouldRenderPersistentBackground = !hoverBackgroundOnly && !isLabel && !hasTransparentBg;
  const buttonPropsOnClick =
    typeof buttonProps?.onClick === 'function'
      ? (buttonProps.onClick as (e: React.SyntheticEvent) => void)
      : undefined;

  return (
    <OptionalTooltip enabled={!!tooltipText} title={tooltipText}>
      <Button
        {...buttonProps}
        isLoading={isLoading}
        disabled={disabled || isLabel}
        variant="outline"
        withIconStart={
          !hideIcon && (
            <Box marginRight={hideText ? '0' : '6'}>
              <StatusButtonIcon
                status={status}
                substatus={substatus}
                includeArchived={includeArchived}
              />
            </Box>
          )
        }
        withIconEnd={
          disabled || isLabel || hideIconEnd ? null : tooltipText ? (
            <UIIcon
              // eslint-disable-next-line deprecation/deprecation -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
              src={Twinkle2Line}
              size={size}
              className={atoms({color: 'Text Subtle', marginLeft: '2'})}
            />
          ) : (
            !hideText && (
              <UIIcon
                src={ChevronRightLine}
                size={size}
                className={atoms({color: 'Text Subtle', marginLeft: '2'})}
              />
            )
          )
        }
        className={cx(styles.statusButton, {
          [styles.removePadding]: isLabel,
          [styles.transparentBg]: hasTransparentBg && !isLabel,
          [styles.hoverChromeOnly]: hoverChromeOnly,
          [styles.disableHoverStyles]: disableHoverStyles,
          [styles.nonInteractive]: nonInteractive,
          [styles.compactIconOnly]: compactIconOnly && !mediumIconOnly,
          [styles.mediumIconOnly]: mediumIconOnly,
        })}
        onClick={(e: React.SyntheticEvent) => {
          buttonPropsOnClick?.(e);
          if (onClick) {
            onClick(e);
          }
          e.stopPropagation();
        }}
        size={size}
        style={{
          backgroundColor: shouldRenderPersistentBackground
            ? getStatusStyle(status).color + (isDark ? '50' : '18')
            : undefined,
        }}
      >
        {!hideText && (
          <Text isBold size={size}>
            {t(status ?? 'no_status').toString()}
          </Text>
        )}
      </Button>
    </OptionalTooltip>
  );
};
